import _applyDecoratedDescriptor from "@babel/runtime/helpers/applyDecoratedDescriptor";
import _uniqBy from "lodash/uniqBy";
var _class;
import { action, computed } from 'mobx';
import { Collection } from 'core/model/Collection';
import api from 'core/util/api';
import AssetTagModel from './AssetTagModel';
let AssetTagCollection = (_class = class AssetTagCollection extends Collection {
  get url() {
    return '/api/ui/assetTags/keys';
  }
  get urlPaths() {
    return {
      fetch: '/api/ui/assetTags/usage',
      destroy: '/api/ui/assetTags/keys'
    };
  }
  get model() {
    return AssetTagModel;
  }
  get defaultSortState() {
    return {
      field: 'keyName',
      direction: 'asc'
    };
  }

  /**
   * Provides a lookup map using a path of assetType.tagId to obtain an array of selectable options
   */
  get filterOptions() {
    const options = {};
    // set up a map we can use to dedupe entries as we process items
    const seen = new Map();
    this.get().forEach(model => {
      (model.get('items') || []).forEach(item => {
        const {
          entity_type,
          value
        } = item;
        const seenKey = `${entity_type}::${model.id}`;
        if (!options[entity_type]) {
          options[entity_type] = {};
        }
        if (!options[entity_type][model.id]) {
          // first access, initialize the array and the seen set
          options[entity_type][model.id] = [];
          seen.set(seenKey, new Set());
        }

        // get the set of seen values for the type and tag
        const seenValues = seen.get(seenKey);
        if (!seenValues?.has(value)) {
          // mark as seen and add to the options array
          seenValues?.add(value);
          options[entity_type][model.id].push({
            label: value,
            value
          });
        }
      });
    });
    return options;
  }

  /**
   * Leverages the lookup map above to provide a unique list of options for the given asset types and tag id.
   * Adds an 'unassigned' option to allow matching assets with no tag association.
   * @param options.assetTypes - An array of asset types to lookup by.
   * @param options.tagId - The tag id to lookup by.
   * @param options.includeUnassigned - Whether to include the 'unassigned' option in the results. Defaults to true.
   * @returns An array of unique options for the given asset types and tag id.
   */
  getTagFilterOptions({
    assetTypes,
    tagId,
    includeUnassigned = true
  }) {
    return [...(includeUnassigned ? [{
      label: 'Unassigned',
      value: '-'
    }] : []), ..._uniqBy(assetTypes.flatMap(assetType => this.filterOptions[assetType]?.[tagId] || []), 'value')];
  }

  /**
   * Leverages the lookup map to provide a unique list of values for the given asset types and tag id.
   * Excludes the 'unassigned' option.
   * @param options.assetTypes - An array of asset types to lookup by.
   * @param options.tagId - The tag id to lookup by.
   * @returns An array of unique values for the given asset types and tag id.
   */
  getTagFilterValues({
    assetTypes,
    tagId
  }) {
    return this.getTagFilterOptions({
      assetTypes,
      tagId,
      includeUnassigned: false
    }).map(option => option.value);
  }

  /**
   * Load items for the specified asset types.
   * @param assetTypes - An array of asset types to load items for.
   * @returns A promise that resolves when the items are loaded.
   */
  loadItems(assetTypes) {
    return this.fetch() // get models with usage data to seed the collection
    .then(() => {
      this.setRequestStatus('fetching');

      // fill in the related items only for the asset types we asked for
      return api.post('/api/ui/assetTags/values/entity', {
        data: {
          asset_types: assetTypes
        }
      }).then(response => {
        Object.entries(response || {}).forEach(([tag_id, entities]) => {
          const model = this.get(tag_id);
          if (model) {
            model.set('items', entities);
          }
        });
      });
    }).finally(() => this.setRequestStatus(null));
  }
}, _applyDecoratedDescriptor(_class.prototype, "filterOptions", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "filterOptions"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "loadItems", [action], Object.getOwnPropertyDescriptor(_class.prototype, "loadItems"), _class.prototype), _class);
export default AssetTagCollection;